import { floor, map, startsWith } from 'lodash';
import sanitize from 'sanitize-filename';
import Pica from 'pica';
import { startCrop, startUpload } from '@client/slices/features/uploads.reducer';
import { trackAnalytics } from '@client/intercom';
import { getUserId } from '@client/selectors/helpers/login';
import { setLoaderIsLoading, setLoaderIsFinishedError, setLoaderIsFinishedSuccess } from '@client/slices/helpers/loaders.reducer';
import { getUploadHash } from '@sda/utils/crypto';
import { getImageCroppedName, getImageOriginalUrl, getImageUncroppedPath } from '@sda/utils/image';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { getImageMimeFromExtension } from '@sda/utils/string';
import { uploadFile } from '@client/listeners/utils/uploads';
import { rotateExif } from '@sda/utils/image/rotateExif';
import { getDateTime } from '@sda/utils/date';
import type { AddListener } from '@client/listeners/lib';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { CropActionPayload, UploadOptions } from '@sda/interfaces/reducer/uploads.reducer';
import type { AppDispatch, RootState } from '@client/store';

const buildImage = (image: string | Blob): Promise<HTMLImageElement> => new Promise((resolve) => {
  const img = document.createElement('img');

  if (typeof image === 'string') {
    img.crossOrigin = 'Anonymous';
  }

  img.src = image instanceof Blob ? URL.createObjectURL(image) : image;

  img.onload = () => resolve(img);
});

/**
 * Credit: https://github.com/DominicTobias/react-image-crop
 *
 * @param {Blob} imageUrl - Image File Object
 * @param {Object} pixelCrop - pixelCrop Object from the 2nd argument of onChange or onComplete
 * @param {String} fileName - Name of the returned file in Promise
 */
const getCroppedImg = async (imageUrl: string, pixelCrop: CropActionPayload['crop'], fileName: string): Promise<Blob> => {
  const htmlImage = await buildImage(imageUrl);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (pixelCrop.unit === '%') {
    const { width, height } = htmlImage;

    const pixelCropWidth = floor((width * pixelCrop.width) / 100);
    const pixelCropHeight = floor((height * pixelCrop.height) / 100);
    const pixelCropX = floor((width * pixelCrop.x) / 100);
    const pixelCropY = floor((height * pixelCrop.y) / 100);

    canvas.width = pixelCropWidth;
    canvas.height = pixelCropHeight;

    ctx.drawImage(
      htmlImage,
      pixelCropX,
      pixelCropY,
      pixelCropWidth,
      pixelCropHeight,
      0,
      0,
      pixelCropWidth,
      pixelCropHeight,
    );
  } else {
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      htmlImage,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob: any) => {
      // eslint-disable-next-line
      blob.name = fileName;

      resolve(blob);
    }, getImageMimeFromExtension(imageUrl));
  });
};

const resizeImage = async (image: HTMLImageElement, mimeType: string): Promise<Blob> => {
  const { height, width } = image;

  const maximumDimension = 1200;

  let dimensions = {
    height,
    width,
  };

  if (dimensions.height > maximumDimension) {
    const ratio = maximumDimension / dimensions.height;

    dimensions.height *= ratio;
    dimensions.width *= ratio;
  }

  if (dimensions.width > maximumDimension) {
    const ratio = maximumDimension / dimensions.width;

    dimensions.height *= ratio;
    dimensions.width *= ratio;
  }

  let canvas = document.createElement('canvas');
  canvas.height = dimensions.height;
  canvas.width = dimensions.width;

  const pica = new Pica();
  canvas = await pica.resize(image, canvas);

  const blob = await pica.toBlob(canvas, mimeType || 'image/png');

  return blob;
};

const cropImage = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>, {
  file, crop, name, options,
}: CropActionPayload) => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const scope = 'image-crop-dialog';
  dispatch(setLoaderIsLoading(scope));

  try {
    const { path: imagePathInBucket, hash } = file;
    const imageUrl = getImageOriginalUrl(getImageUncroppedPath(imagePathInBucket));

    const croppedImageName = getImageCroppedName(imagePathInBucket);
    const croppedImage = await getCroppedImg(imageUrl, crop, croppedImageName);
    const userId = getUserId(state);

    const response = await uploadFile(listenerApi, `${userId}/${name}`, croppedImageName, croppedImage);

    if (response.status === true) {
      const newDownloadUrl = `${userId}/${name}/${croppedImageName}`;

      if (options?.callback) {
        options.callback({
          hash,
          path: newDownloadUrl,
          updatedAt: getDateTime().toJSDate(),
        });

        if (options.scope) {
          dispatch(setLoaderIsFinishedSuccess(options.scope));
        }
      }
    } else {
      throw new Error('Unable to save cropped file.');
    }
  } catch (e) {
    console.log(e);

    dispatch(setLoaderIsFinishedError(scope, e));
    showSnackbar(e, 'error');
  }

  dispatch(setLoaderIsFinishedSuccess(scope));
};

/**
 * Upload a single file
 * @param  {string} objectName
 * @param  {any} file
 * @param  {object} options
 * @param  {boolean} options.clear
 * @param  {number} [options.targetRatio] defined as: width / height
 */
const handleUpload = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>, uploadId: string, file: File, options: UploadOptions) => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  // Namespace all uploads by user ID
  const userId = getUserId(state);
  const userPath = `${userId}/${uploadId}`;

  const fileName = sanitize(`${file.lastModified}-${file.name}`);

  if (options?.scope) {
    dispatch(setLoaderIsLoading(options.scope));
  }

  try {
    let newFile: File | Blob = file;

    if (startsWith(file.type, 'image')) {
      let rotation: string;

      try {
        rotation = await rotateExif(file);
      } catch {
        // do nothing
      }

      const img = await buildImage(rotation || file);

      newFile = await resizeImage(img, file.type);
    }

    if (startsWith(file.type, 'image') || file.size < parseFloat(process.env.MAX_UPLOAD_FILE_SIZE)) {
      const response = await uploadFile(listenerApi, userPath, fileName, newFile);

      if (response.status === true) {
        const hash = getUploadHash(fileName);

        if (startsWith(file.type, 'image')) {
          const { targetRatio } = options;

          if (targetRatio) {
            // Crop image after uploading
            const imgBucketPath = `${userPath}/${fileName}`;
            const img = await buildImage(getImageOriginalUrl(imgBucketPath));

            const fileObjectForCrop = {
              hash,
              path: imgBucketPath,
            };

            const { width, height } = img;
            const imgRatio = width / height;
            const imageCentre = { x: width / 2, y: height / 2 };

            // Start the crop at the entire dimensions of the image
            let cropData = {
              x: 0, y: 0, width, height,
            };

            // Generate the maximum dimensions of the crop based on target aspect ratio
            if (imgRatio > targetRatio) {
              cropData.width = height * targetRatio;
            } else if (imgRatio < targetRatio) {
              cropData.height = width / targetRatio;
            }

            // Set the bounds of the crop based on the target ratio and current ratio in order to centre the image
            if (targetRatio > 1) {
              cropData.y = Math.round(imageCentre.y - (cropData.height / 2));
              cropData.x = Math.round(imageCentre.x - (cropData.width / 2));
            } else if (targetRatio === 1) {
              if (width > height) { // Target is square, source is landscape
                cropData.x = Math.round(imageCentre.x - height / 2);
              } else if (width < height) { // Target is square, source is portrait
                cropData.y = Math.round(imageCentre.y - width / 2);
              }
            } else if (width >= height) { // Target is portrait, source is landscape
              cropData.x = Math.round(imageCentre.x - (cropData.width / 2));
            }

            await cropImage(listenerApi, {
              file: fileObjectForCrop, crop: cropData, name: uploadId, options,
            });
          } else if (options?.callback) {
            options.callback({
              hash,
              path: response.downloadUrl,
            });

            if (options.scope) {
              dispatch(setLoaderIsFinishedSuccess(options.scope));
            }
          }
        } else if (options?.callback) {
          options.callback({
            hash,
            path: response.downloadUrl,
          });

          if (options.scope) {
            dispatch(setLoaderIsFinishedSuccess(options.scope));
          }
        }
      } else {
        throw new Error(response.message);
      }
    } else {
      throw new Error('The file you have attached is too large. Max file size is 20MB.');
    }
  } catch (e) {
    let body = e.message;

    if (body.includes('fingerprinting protection')) {
      body = 'Your browser has blocked a feature required for us to optimize image uploads. If you are using Brave, please navigate to the shield icon in your address bar and unblock browser fingerprinting. If you are not using Brave, please reach out to support.';
    }

    console.log(e);
    showSnackbar(body, 'error');

    if (options?.scope) {
      dispatch(setLoaderIsFinishedError(options.scope, e));
    }
  }
};

export const uploadsListeners = (addListener: AddListener) => [
  /**
   * Upload a file. Uploads should come with an "ID" of some sort... a key to identify them back to components that need
   * the url upon completion. When the upload is complete, we pass that ID back so the reducer can store it somewhere
   * the component can find it.
   * @param action
   */
  addListener({
    actionCreator: startUpload,
    effect: async (action, listenerApi) => {
      const {
        files, name, options,
      } = action.payload;

      const tasks = map(files, (fileObject) => listenerApi.fork(async () => {
        await handleUpload(listenerApi, name, fileObject, options);
      }));

      await Promise.all(tasks);

      trackAnalytics(startUpload);
    },
  }),

  addListener({
    actionCreator: startCrop,
    effect: async (action, listenerApi) => {
      await cropImage(listenerApi, action.payload);

      trackAnalytics(startCrop);
    },
  }),
];
